import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateAfterDependencyChange } from '@/features/items/utils/itemCacheUtils';
import { DependencyChangeKind } from '@/features/items/utils/refreshLoadedDependencies';
import { InvalidDependencyPairError, SelfDependencyError } from './dependencyErrors';
import { deleteDependency } from './dependencyOperations';

interface DeleteDependencyVariables {
  predecessorId: number;
  successorId: number;
}

interface UseDeleteDependencyMutationParams {
  currentItemId: number;
  options?: Omit<UseMutationOptions<void, Error, DeleteDependencyVariables>, 'mutationFn'>;
}

export const useDeleteDependencyMutation = ({
  currentItemId,
  options,
}: UseDeleteDependencyMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteDependencyVariables>({
    ...options,
    mutationFn: async ({ predecessorId, successorId }) => {
      const isPredecessorCurrent = predecessorId === currentItemId;
      const isSuccessorCurrent = successorId === currentItemId;
      if (isPredecessorCurrent && isSuccessorCurrent) {
        throw new SelfDependencyError();
      }
      if (!isPredecessorCurrent && !isSuccessorCurrent) {
        throw new InvalidDependencyPairError();
      }
      const otherId = isPredecessorCurrent ? successorId : predecessorId;
      const actualPredecessorId = isPredecessorCurrent ? currentItemId : otherId;
      const actualSuccessorId = isPredecessorCurrent ? otherId : currentItemId;
      await deleteDependency({
        itemId: currentItemId,
        predecessorId: actualPredecessorId,
        successorId: actualSuccessorId,
      });
    },
    onSuccess: (data, variables, ...rest) => {
      const otherId =
        variables.predecessorId === currentItemId ? variables.successorId : variables.predecessorId;
      // Defer invalidation to the next microtask so the caller's UI updates
      // commit before suspense queries refetch.
      queueMicrotask(() =>
        invalidateAfterDependencyChange(
          queryClient,
          currentItemId,
          otherId,
          DependencyChangeKind.Remove,
        ),
      );
      options?.onSuccess?.(data, variables, ...rest);
    },
  });
};
